import {Component, effect, Inject, inject, Injector, Optional, PLATFORM_ID, runInInjectionContext} from '@angular/core';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {AuthenticationService} from '../services/authentication.service';
import {NotificationService} from '../notification/notification.service';
import {SpinnerService} from '../services/spinner.service';
import {toSignal} from '@angular/core/rxjs-interop';
import {catchError, of} from 'rxjs';
import {MAT_DIALOG_DATA, MatDialogContent, MatDialogRef} from '@angular/material/dialog';
import {NotificationType} from '../enum/notification-type.enum';
import {isPlatformBrowser} from '@angular/common';
import {ActivatedRoute} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {FieldNamesEnum} from '../enum/field-names.enum';
import {environment} from '../../environments/environment';
import {THEME_CONFIG} from '../../environments/tokens/theme.token';
import {MatIcon} from '@angular/material/icon';
import {CdkScrollable} from '@angular/cdk/scrolling';
import {MatButton} from '@angular/material/button';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatOption, MatSelect} from '@angular/material/select';
import {MatInput} from '@angular/material/input';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import {NgxMatInputTelComponent} from 'ngx-mat-input-tel';
import {MatTooltip} from '@angular/material/tooltip';
import {OrderContextStore} from '../store/order-context.store';
import {TypeOrderEnum} from '../enum/type-order.enum';
import {AdvertisementFlowService} from '../services/advertisement-flow.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  imports: [
    MatIcon,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatButton,
    MatProgressSpinner,
    MatSelect,
    MatOption,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepicker,
    NgxMatInputTelComponent,
    MatTooltip,
  ],
  standalone: true
})
export class LoginComponent {
  private fb = inject(FormBuilder);
  private authenticationService = inject(AuthenticationService);
  private notificationService = inject(NotificationService);
  private spinnerService = inject(SpinnerService);
  private injector = inject(Injector);
  hide = true;
  hideNew = true;
  hideRetype = true;
  isSubmitting = false;
  hideConfirm = true;
  isResetMode = false;
  isResetPasswordMode = false;
  resetKey: string | null = null;
  resetToken: string | null = null;
  resetPasswordForm = this.fb.group({
    newPassword: ['', [Validators.required, Validators.minLength(8)]],
    retypePassword: ['', Validators.required],
  }, {validators: this.passwordMatchValidator});
  redirectUri = environment.redirectUri;
  protected readonly themeConfig = inject(THEME_CONFIG);
  private orderContextStore = inject(OrderContextStore);

  private adFlowService = inject(AdvertisementFlowService);

  constructor(
    @Optional() public dialogRef: MatDialogRef<LoginComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    @Inject(PLATFORM_ID) private platformId: Object,
    private route: ActivatedRoute,
  ) {
    if (this.data?.isResetPasswordMode) {
      this.isResetPasswordMode = true;
      this.resetKey = this.data.resetKey;
      this.resetToken = this.data.resetToken;
      this.isLoginMode = false;
      this.isResetMode = false;
    }
  }

  isLoginMode = true;

  loginForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', Validators.required],
  });

  registerForm = this.fb.group({
      firstname: ['', Validators.required],
      lastname: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      birthdate: [null as Date | null, Validators.required],
      phone: ['', Validators.required],
      gender: ['', Validators.required],
    }
  );
  resetForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
  });

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      if (params['key'] && params['token'] && !this.isResetPasswordMode) {
        this.isResetPasswordMode = true;
        this.resetKey = params['key'];
        this.resetToken = params['token'];
        this.isLoginMode = false;
      }
    });
  }

  get passwordValid(): boolean {
    const password = this.registerForm.get('password')?.value ?? '';
    const hasMinLength = password.length >= 8;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return hasMinLength && hasUpper && hasLower && hasNumber && hasSpecial;
  }

  get newPasswordValid(): boolean {
    const password = this.resetPasswordForm.get('newPassword')?.value ?? '';
    const hasMinLength = password.length >= 8;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return hasMinLength && hasUpper && hasLower && hasNumber && hasSpecial;
  }

  passwordMatchValidator(formGroup: FormGroup) {
    const newPassword = formGroup.get('newPassword')?.value;
    const retypePassword = formGroup.get('retypePassword')?.value;

    if (!newPassword || !retypePassword) {
      return null;
    }

    return newPassword === retypePassword ? null : {mismatch: true};
  }

  toggleMode() {
    this.isLoginMode = !this.isLoginMode;
    this.loginForm.reset();
    this.registerForm.reset();
  }

  forgotPassword() {
    this.isLoginMode = false;
    this.isResetMode = true;
    this.loginForm.reset();
    this.resetForm.patchValue({
      email: this.loginForm.get('email')?.value || ''
    });
  }

  onBirthdateChange(event: any) {
    const date = event.value;
    if (date) {
      this.registerForm.get('birthdate')?.setValue(date, {emitEvent: false});
    }
  }

  onBirthdateInput(event: any) {
    const inputValue = event.target.value;
    const parsedDate = this.parseManualDate(inputValue);

    if (parsedDate) {
      this.registerForm.get('birthdate')?.setValue(parsedDate, {emitEvent: false});
    }
  }

  parseManualDate(input: string): Date | null {
    const cleaned = input.replace(/[^0-9]/g, '');

    if (cleaned.length >= 8) {
      const day = parseInt(cleaned.substring(0, 2), 10);
      const month = parseInt(cleaned.substring(2, 4), 10) - 1;
      const year = parseInt(cleaned.substring(4, 8), 10);

      const date = new Date(year, month, day);

      if (
        date.getDate() === day &&
        date.getMonth() === month &&
        date.getFullYear() === year &&
        year >= 1900 &&
        year <= new Date().getFullYear()
      ) {
        return date;
      }
    }

    return null;
  }

  onResetPassword() {
    if (this.resetForm.invalid) {
      this.resetForm.markAllAsTouched();
      return;
    }
    this.isSubmitting = true;
    this.spinnerService.show();

    const email = this.resetForm.get('email')?.value ?? '';

    runInInjectionContext(this.injector, () => {
      const resetSignal = toSignal(
        this.authenticationService.resetPassword(this.redirectUri, email).pipe(
          catchError(err => {
            const details = err?.error?.details?.message;

            if (details?.includes('Customer not found')) {
              this.notificationService.sendNotification(
                NotificationType.WARNING,
                'Cet e-mail n’existe pas, merci de taper un e-mail correct', '');
            } else {
              this.notificationService.errorHandler(err);
            }

            this.isSubmitting = false;
            this.spinnerService.hide();
            return of(null);
          })
        ),
        {initialValue: null}
      );

      effect(() => {
        const result = resetSignal();
        if (!result) return;

        this.spinnerService.hide();
        this.notificationService.sendNotification(
          NotificationType.SUCCESS,
          "Un email de réinitialisation a été envoyé !",
          ""
        );

        this.dialogRef.close();

        this.isSubmitting = false;
      });
    });
  }

  onRegister() {
    if (!this.registerForm.get('gender')?.value) {
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        'Veuillez saisir votre civilité',
        ''
      );
      this.registerForm.get('gender')?.markAsTouched();
      return;
    }
    if (!this.registerForm.get('birthdate')?.value) {
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        'Veuillez saisir votre date de naissance',
        ''
      );
      this.registerForm.get('birthdate')?.markAsTouched();
      return;
    }

    if (!this.registerForm.get('firstname')?.value) {
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        'Veuillez saisir votre prénom',
        ''
      );
      this.registerForm.get('firstname')?.markAsTouched();
      return;
    }
    if (!this.registerForm.get('lastname')?.value) {
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        'Veuillez saisir votre nom',
        ''
      );
      this.registerForm.get('lastname')?.markAsTouched();
      return;
    }
    if (!this.registerForm.get('email')?.value) {
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        'Veuillez saisir votre E-mail',
        ''
      );
      this.registerForm.get('phone')?.markAsTouched();
      return;
    }
    if (!this.registerForm.get('phone')?.value) {
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        'Veuillez saisir votre numéro de téléphone',
        ''
      );
      this.registerForm.get('phone')?.markAsTouched();
      return;
    }

    if (!this.registerForm.get('password')?.value) {
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        'Veuillez saisir votre mot de passe',
        ''
      );
      this.registerForm.get('password')?.markAsTouched();
      return;
    }
    const f = this.registerForm.value;
    const request = {...f};
    this.isSubmitting = true;

    this.spinnerService.show();

    runInInjectionContext(this.injector, () => {
      const registerSignal = toSignal(
        this.authenticationService.registerCustomer(this.redirectUri, request).pipe(
          catchError((httpErrorResponse: HttpErrorResponse) => {
            const error = httpErrorResponse.error;

            if (error?.details) {
              const detailsArray = Array.isArray(error.details) ? error.details : [error.details];

              detailsArray.forEach((detail: any) => {
                if (detail.message) {
                  const fieldTranslation = detail.field && FieldNamesEnum[detail.field as keyof typeof FieldNamesEnum]
                    ? FieldNamesEnum[detail.field as keyof typeof FieldNamesEnum]
                    : detail.field;

                  const msg = fieldTranslation
                    ? `${fieldTranslation} : ${detail.message}`
                    : detail.message;

                  this.notificationService.sendNotification(NotificationType.ERROR, msg, '');
                }
              });
            } else {
              this.notificationService.errorHandler(httpErrorResponse);
            }

            this.isSubmitting = false;
            this.spinnerService.hide();
            return of(null);
          })
        ),
        {initialValue: null}
      );

      effect(() => {
        const result = registerSignal();
        if (!result) return;

        this.spinnerService.hide();
        this.notificationService.sendNotification(
          NotificationType.SUCCESS,
          result.body?.message || '',
          ''
        );

        this.loginForm.patchValue({
          email: f.email,
          password: f.password,
        });

        setTimeout(() => this.onLogin(), 500);
        this.isSubmitting = false;
      });
    });
  }

  onLogin() {
    const {email, password} = this.loginForm.value;
    this.isSubmitting = true;

    this.spinnerService.show();

    runInInjectionContext(this.injector, () => {
      const loginSignal = toSignal(
        this.authenticationService.login(email!, password!).pipe(
          catchError((err) => {
            this.notificationService.errorHandler(err);
            this.isSubmitting = false;
            this.spinnerService.hide();
            return of(null);
          })
        ),
        {initialValue: null}
      );
      effect(() => {
        const result = loginSignal();
        if (!result) return;

        this.spinnerService.hide();

        const token: string | null | undefined =
          !Array.isArray(result?.body?.data) ? result?.body?.data?.access : undefined;
        const refreshToken: string | null | undefined =
          !Array.isArray(result?.body?.data) ? result?.body?.data?.refresh : undefined;
        this.authenticationService.saveToken(token);
        this.authenticationService.saveRefreshToken(refreshToken);

        if (isPlatformBrowser(this.platformId)) {
          this.checkAndShowAdvertisement();
        }

        if (isPlatformBrowser(this.platformId) && this.dialogRef) {
          this.dialogRef.close(true);
        }
        this.isSubmitting = false;
      });
    });
  }

  private checkAndShowAdvertisement(): void {
    const cityId = this.orderContextStore.cityId();
    const isDelivery = this.orderContextStore.orderType() === TypeOrderEnum.DELIVERY;

    this.adFlowService.checkAndShowAdvertisements(cityId, isDelivery);
  }

  onResetNewPassword() {
    if (this.resetPasswordForm.invalid) {
      this.resetPasswordForm.markAllAsTouched();
      return;
    }

    if (!this.resetKey || !this.resetToken) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'Lien invalide ou expiré', '');
      return;
    }

    this.isSubmitting = true;
    this.spinnerService.show();

    const payload = {
      key: this.resetKey,
      token: this.resetToken,
      newPassword: this.resetPasswordForm.get('newPassword')?.value,
      retypePassword: this.resetPasswordForm.get('retypePassword')?.value,
    };

    runInInjectionContext(this.injector, () => {
      const resetSignal = toSignal(
        this.authenticationService.updatePassword(payload).pipe(
          catchError(err => {
            this.notificationService.errorHandler(err);
            this.isSubmitting = false;
            this.spinnerService.hide();
            return of(null);
          })
        ),
        {initialValue: null}
      );

      effect(() => {
        const result = resetSignal();
        if (!result) return;

        this.spinnerService.hide();
        this.isSubmitting = false;

        this.notificationService.sendNotification(
          NotificationType.SUCCESS,
          'Mot de passe réinitialisé avec succès !',
          'Vous pouvez maintenant vous connecter.'
        );

        this.isResetPasswordMode = false;
        this.isLoginMode = true;
        this.isResetMode = false;
      });
    });
  }

  get phoneControl() {
    return this.registerForm.get('phone')!;
  }

  get canSubmit(): boolean {
    return this.registerForm.valid && this.passwordValid;
  }
}
